import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import {
    CheckCircle2, Circle, Clock, FileText, ExternalLink, Loader2, Lock,
    ThumbsUp, Undo2, Info, Link2, ChevronDown, ChevronUp, CornerDownRight
} from "lucide-react";
import CommentsBlock from "@/components/CommentsBlock";
import { displayTitle, revisionLabel } from "@/lib/naming";

// public — no auth needed
const publicApi = axios.create({ baseURL: API, withCredentials: true });

export default function PublicPortal() {
    const { shareToken } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [locked, setLocked] = useState(false);
    const [password, setPassword] = useState("");
    const [unlocking, setUnlocking] = useState(false);
    const [unlockError, setUnlockError] = useState("");
    const [changesFor, setChangesFor] = useState(null); // approval object
    const [feedback, setFeedback] = useState("");
    const [saving, setSaving] = useState(false);
    const [expandedHistory, setExpandedHistory] = useState({});
    const mountedOnce = useRef(false);

    const load = useCallback(async () => {
        try {
            const { data } = await publicApi.get(`/public/portal/${shareToken}`);
            setData(data);
            setLocked(false);
        } catch (e) {
            if (e.response?.status === 404) setNotFound(true);
            else if (e.response?.status === 401) setLocked(true);
        } finally { setLoading(false); }
    }, [shareToken]);
    useEffect(() => {
        // Guard against React.StrictMode double-invoking the initial load in dev,
        // which would race a second GET before the dedup cookie is committed.
        if (mountedOnce.current) return;
        mountedOnce.current = true;
        load();
    }, [load]);

    const unlock = async () => {
        if (!password) return;
        setUnlocking(true); setUnlockError("");
        try {
            await publicApi.post(`/public/portal/${shareToken}/unlock`, { password });
            setPassword("");
            await load();
        } catch (e) {
            setUnlockError(e.response?.status === 429
                ? "Too many attempts. Please try again in a few minutes."
                : "Incorrect password.");
        } finally { setUnlocking(false); }
    };

    // Map approval_id -> version_number derived from deliverables[].versions[]
    const versionByApproval = useMemo(() => {
        const m = {};
        const dels = data?.portal?.deliverables || [];
        for (const d of dels) {
            for (const v of (d.versions || [])) {
                if (v.approval_id) m[v.approval_id] = v.version_number;
            }
        }
        return m;
    }, [data]);

    const approve = async (a) => {
        setSaving(true);
        try {
            await publicApi.post(`/public/portal/${shareToken}/approvals/${a.approval_id}/approve`);
            toast.success(`Approved '${a.title}'`);
            await load();
        } catch {
            toast.error("Could not submit approval");
        } finally { setSaving(false); }
    };

    const submitChanges = async () => {
        if (!feedback.trim()) { toast.error("Please add a note for the team"); return; }
        setSaving(true);
        try {
            await publicApi.post(`/public/portal/${shareToken}/approvals/${changesFor.approval_id}/request-changes`,
                { feedback: feedback.trim() });
            toast.success("Feedback sent to your team");
            setChangesFor(null);
            setFeedback("");
            await load();
        } catch {
            toast.error("Could not send feedback");
        } finally { setSaving(false); }
    };

    if (loading) return (
        <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-slate-200 border-t-[#C55A46] rounded-full animate-spin" />
        </div>
    );
    if (notFound) return (
        <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6">
            <div className="text-center max-w-md">
                <p className="overline mb-3">Portal not found</p>
                <h1 className="font-display text-3xl tracking-tight text-slate-900">This link is no longer active.</h1>
                <p className="text-slate-500 mt-3">Please reach out to your team for an updated link.</p>
            </div>
        </div>
    );
    if (locked) return (
        <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6">
            <div className="max-w-md w-full">
                <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
                    <div className="w-11 h-11 rounded-xl bg-slate-50 flex items-center justify-center mx-auto mb-4">
                        <Lock className="w-5 h-5 text-slate-500" />
                    </div>
                    <p className="overline mb-2">Protected portal</p>
                    <h1 className="font-display text-2xl tracking-tight text-slate-900">Enter password to continue</h1>
                    <p className="text-slate-500 mt-2 text-sm">Ask your team for the passcode if you don't have it.</p>
                    <form className="mt-6 space-y-3" onSubmit={(e) => { e.preventDefault(); unlock(); }}>
                        <input
                            type="password" value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            data-testid="portal-lock-input"
                            className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm ring-terra"
                            placeholder="Password" autoComplete="current-password" autoFocus
                        />
                        {unlockError && (
                            <p data-testid="portal-lock-error" className="text-xs text-rose-600 text-left">{unlockError}</p>
                        )}
                        <button
                            type="submit" disabled={unlocking || !password}
                            data-testid="portal-lock-submit"
                            className="btn-terra w-full rounded-lg py-2.5 text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-70">
                            {unlocking && <Loader2 className="w-4 h-4 animate-spin" />} Unlock portal
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
    if (!data) return null;

    const p = data.portal || {};
    const accent = data.agency?.brand_color || "#C55A46";
    const agencyName = data.agency?.company_name || "";

    return (
        <div className="min-h-screen bg-[#FAFAFA]">
            {/* Client-facing header — branded as agency, not ClientSpace */}
            <header className="glass-nav sticky top-0 z-30">
                <div className="max-w-5xl mx-auto px-6 lg:px-10 py-4 flex items-center gap-3">
                    <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-md flex items-center justify-center text-white font-semibold text-xs"
                             style={{ backgroundColor: accent }}>
                            {(agencyName || data.company_name)[0]}
                        </div>
                        <span className="font-display font-semibold tracking-tight text-slate-900">
                            {agencyName || "Your project portal"}
                        </span>
                    </div>
                    <span className="ml-auto text-xs text-slate-400 hidden sm:inline">Portal for {data.company_name}</span>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
                {/* Portal hero */}
                <section className="rounded-2xl border border-slate-200 bg-white p-8 mb-6 relative overflow-hidden">
                    <div className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full blur-3xl"
                         style={{ backgroundColor: `${accent}22` }} />
                    <p className="overline mb-2">Project portal</p>
                    <h1 className="font-display text-4xl md:text-5xl tracking-tight text-slate-900">{data.project_name}</h1>
                    <p className="text-slate-500 mt-2">for {data.company_name}</p>
                    {agencyName && (
                        <p className="text-xs text-slate-400 mt-4">Delivered by {agencyName}</p>
                    )}
                </section>

                <Tabs defaultValue="overview" className="w-full">
                    <TabsList className="bg-white border border-slate-200 rounded-lg p-1">
                        <TabsTrigger value="overview" data-testid="pub-tab-overview" className="rounded-md">Overview</TabsTrigger>
                        <TabsTrigger value="progress" data-testid="pub-tab-progress" className="rounded-md">Progress</TabsTrigger>
                        <TabsTrigger value="deliverables" data-testid="pub-tab-deliverables" className="rounded-md">Deliverables</TabsTrigger>
                        <TabsTrigger value="approvals" data-testid="pub-tab-approvals" className="rounded-md">
                            Approvals
                            {(p.approvals || []).some(a => a.status === "pending") && (
                                <span className="ml-2 w-1.5 h-1.5 rounded-full inline-block" style={{ backgroundColor: accent }} />
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="files" data-testid="pub-tab-files" className="rounded-md">Files</TabsTrigger>
                    </TabsList>

                    <TabsContent value="overview" className="mt-4">
                        <div className="grid md:grid-cols-3 gap-4">
                            <Stat title="Status" value={p.overview?.status || "Active"} />
                            <Stat title="Next milestone" value={p.overview?.next_milestone || "—"} />
                            <Stat title="Overall progress" value={`${p.progress?.percent || 0}%`} />
                            <div className="md:col-span-3 rounded-xl border border-slate-200 bg-white p-6">
                                <p className="overline mb-2">Summary</p>
                                <p className="text-slate-700 leading-relaxed">{p.overview?.summary}</p>
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="progress" className="mt-4">
                        <div className="rounded-xl border border-slate-200 bg-white p-8">
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <p className="overline mb-1">Project progress</p>
                                    <h3 className="font-display text-2xl tracking-tight">{p.progress?.percent || 0}% complete</h3>
                                </div>
                            </div>
                            <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-8">
                                <div className="h-full transition-[width] duration-500"
                                     style={{ width: `${p.progress?.percent || 0}%`, backgroundColor: accent }} />
                            </div>
                            <div className="grid md:grid-cols-4 gap-3">
                                {(p.progress?.phases || []).map((ph) => (
                                    <PhaseChip key={ph.name} phase={ph} accent={accent} />
                                ))}
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="deliverables" className="mt-4">
                        {(p.deliverables || []).length === 0 ? (
                            <EmptyBox text="No deliverables yet." />
                        ) : (
                            <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                                {(p.deliverables || []).map((d) => {
                                    const versions = (d.versions || []).slice().sort((a, b) => a.version_number - b.version_number);
                                    const cv = versions.length ? versions[versions.length - 1] : null;
                                    const displayStatus = cv ? cv.status : d.status;
                                    const showHistory = !!expandedHistory[d.deliverable_id];
                                    return (
                                    <div key={d.deliverable_id} data-testid={`pub-deliverable-${d.deliverable_id}`}
                                         className="px-6 py-5">
                                        <div className="flex items-start gap-4">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="text-sm font-medium text-slate-900">{displayTitle(d.title)}</p>
                                                    {cv && (
                                                        <span data-testid={`pub-deliverable-version-${d.deliverable_id}`}
                                                              className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                                                            Current: {revisionLabel(cv.version_number)}
                                                        </span>
                                                    )}
                                                </div>
                                                {d.description && <p className="text-xs text-slate-500 mt-1 leading-relaxed">{d.description}</p>}
                                                <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                                                    {d.due && <span>Due {d.due}</span>}
                                                    {(cv?.file_name || d.file_name) && (
                                                        <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3" />{cv?.file_name || d.file_name}</span>
                                                    )}
                                                    {(cv?.link || d.link) && (
                                                        <a href={cv?.link || d.link} target="_blank" rel="noreferrer"
                                                           className="inline-flex items-center gap-1 hover:text-slate-700">
                                                            <Link2 className="w-3 h-3" /> Open link
                                                        </a>
                                                    )}
                                                </div>
                                                {cv?.agency_reply && (
                                                    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 flex gap-2">
                                                        <CornerDownRight className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />
                                                        <div>
                                                            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Note from your team</p>
                                                            <p className="mt-1 whitespace-pre-wrap leading-relaxed">{cv.agency_reply}</p>
                                                        </div>
                                                    </div>
                                                )}
                                                {cv && (
                                                    <CommentsBlock
                                                        comments={cv.comments || []}
                                                        versionLabel={revisionLabel(cv.version_number)}
                                                        testidPrefix={`pub-comments-${d.deliverable_id}-${cv.version_id}`}
                                                        accent={accent}
                                                        onSubmit={async (text) => {
                                                            const { data } = await publicApi.post(
                                                                `/public/portal/${shareToken}/deliverables/${d.deliverable_id}/versions/${cv.version_id}/comments`,
                                                                { text },
                                                            );
                                                            load();
                                                            return data;
                                                        }}
                                                    />
                                                )}
                                                {versions.length > 1 && (
                                                    <div className="mt-3">
                                                        <button
                                                            data-testid={`pub-toggle-history-${d.deliverable_id}`}
                                                            onClick={() => setExpandedHistory({ ...expandedHistory, [d.deliverable_id]: !showHistory })}
                                                            className="text-xs font-medium text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
                                                            {showHistory ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                                            Version history ({versions.length})
                                                        </button>
                                                        {showHistory && (
                                                            <ul data-testid={`pub-version-history-${d.deliverable_id}`}
                                                                className="mt-3 space-y-3 border-l-2 border-slate-100 pl-4">
                                                                {versions.map((v) => (
                                                                    <li key={v.version_id} data-testid={`pub-version-row-${d.deliverable_id}-${v.version_number}`}
                                                                        className="text-xs">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="text-slate-600 font-medium">{revisionLabel(v.version_number)}</span>
                                                                            <StatusPill status={v.status} />
                                                                            <span className="text-slate-400 ml-auto">{formatDate(v.submitted_at)}</span>
                                                                        </div>
                                                                        {(v.file_name || v.link) && (
                                                                            <div className="mt-1 text-slate-500 flex items-center gap-2">
                                                                                {v.file_name && <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3" />{v.file_name}</span>}
                                                                                {v.link && <a href={v.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-slate-700"><Link2 className="w-3 h-3" /> Open</a>}
                                                                            </div>
                                                                        )}
                                                                        {v.feedback && (
                                                                            <div className="mt-1 text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                                                                                <span className="font-medium">Your feedback:</span> {v.feedback}
                                                                            </div>
                                                                        )}
                                                                        {v.agency_reply && (
                                                                            <div className="mt-1 text-slate-700 bg-slate-50 border border-slate-200 rounded p-2 flex gap-2">
                                                                                <CornerDownRight className="w-3 h-3 mt-0.5 shrink-0 text-slate-400" />
                                                                                <div><span className="font-medium">Team reply:</span> {v.agency_reply}</div>
                                                                            </div>
                                                                        )}
                                                                        {v.version_id !== cv.version_id && (
                                                                            <CommentsBlock
                                                                                comments={v.comments || []}
                                                                                versionLabel={revisionLabel(v.version_number)}
                                                                                testidPrefix={`pub-comments-${d.deliverable_id}-${v.version_id}`}
                                                                                accent={accent}
                                                                                onSubmit={async (text) => {
                                                                                    const { data } = await publicApi.post(
                                                                                        `/public/portal/${shareToken}/deliverables/${d.deliverable_id}/versions/${v.version_id}/comments`,
                                                                                        { text },
                                                                                    );
                                                                                    load();
                                                                                    return data;
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                            <StatusPill status={displayStatus} />
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="approvals" className="mt-4">
                        {(p.approvals || []).length === 0 ? (
                            <EmptyBox text="No approvals pending." />
                        ) : (
                            <div className="space-y-3">
                                {(p.approvals || []).map((a) => {
                                    const vNum = versionByApproval[a.approval_id];
                                    return (
                                    <div key={a.approval_id} data-testid={`pub-approval-${a.approval_id}`}
                                         className="rounded-xl border border-slate-200 bg-white p-6">
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="text-sm font-medium text-slate-900">{displayTitle(a.title)}</p>
                                                    {vNum != null && (
                                                        <span data-testid={`pub-approval-version-${a.approval_id}`}
                                                              className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                                                            {revisionLabel(vNum)}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-slate-400 mt-1">Requested {formatDate(a.requested_at)}</p>
                                                {a.feedback && a.status === "changes_requested" && (
                                                    <div className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                                                        <span className="font-medium">Your feedback:</span> {a.feedback}
                                                    </div>
                                                )}
                                            </div>
                                            <StatusPill status={a.status} />
                                        </div>
                                        {a.status === "pending" && (
                                            <div className="mt-4 flex items-center gap-2">
                                                <button onClick={() => approve(a)} disabled={saving}
                                                        data-testid={`pub-approve-${a.approval_id}`}
                                                        className="btn-terra text-sm font-medium px-4 py-2 rounded-lg inline-flex items-center gap-2 disabled:opacity-70"
                                                        style={{ backgroundColor: accent }}>
                                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsUp className="w-4 h-4" />}
                                                    Approve
                                                </button>
                                                <button onClick={() => { setChangesFor(a); setFeedback(""); }}
                                                        data-testid={`pub-request-changes-${a.approval_id}`}
                                                        className="text-sm font-medium border border-slate-200 rounded-lg px-4 py-2 hover:border-slate-300 transition-colors inline-flex items-center gap-2">
                                                    <Undo2 className="w-4 h-4" /> Request changes
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="files" className="mt-4">
                        {(p.files || []).length === 0 ? (
                            <EmptyBox text="No files shared yet." />
                        ) : (
                            <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                                {(p.files || []).map((f) => (
                                    <div key={f.file_id} data-testid={`pub-file-${f.file_id}`}
                                         className="flex items-center gap-4 px-6 py-4">
                                        <div className="w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center">
                                            <FileText className="w-4 h-4 text-slate-500" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium text-slate-900 truncate">{f.name}</p>
                                            <p className="text-xs text-slate-400">
                                                {f.size}{f.size && f.note ? " · " : ""}{f.note}
                                            </p>
                                        </div>
                                        {f.link ? (
                                            <a href={f.link} target="_blank" rel="noreferrer"
                                               className="text-slate-500 hover:text-slate-900 p-2 rounded-md inline-flex items-center gap-1 text-xs font-medium">
                                                Open <ExternalLink className="w-3.5 h-3.5" />
                                            </a>
                                        ) : (
                                            <span className="text-xs text-slate-400 italic">Ask your team</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>

                <footer className="mt-16 flex items-center justify-between text-xs text-slate-400">
                    <div className="inline-flex items-center gap-1.5">
                        <Info className="w-3.5 h-3.5" /> Live portal — refresh to see updates
                    </div>
                    <span>Delivered by {agencyName || "your team"}</span>
                </footer>
            </main>

            {/* Request changes dialog */}
            <Dialog open={!!changesFor} onOpenChange={(v) => !v && setChangesFor(null)}>
                <DialogContent className="sm:max-w-[500px] rounded-xl border-slate-200">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl tracking-tight">Request changes</DialogTitle>
                        <DialogDescription className="text-slate-500">
                            Tell the team what to adjust. Be as specific as you like.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2 pt-2">
                        <Label className="text-slate-700">Feedback for the team</Label>
                        <Textarea rows={5} value={feedback} onChange={(e) => setFeedback(e.target.value)}
                                  data-testid="pub-changes-feedback"
                                  className="ring-terra" placeholder="e.g. Please try a warmer palette on the hero." />
                    </div>
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button onClick={() => setChangesFor(null)}
                                className="px-4 py-2 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors"
                                data-testid="pub-changes-cancel">
                            Cancel
                        </button>
                        <button onClick={submitChanges} disabled={saving}
                                data-testid="pub-changes-submit"
                                className="btn-terra rounded-lg px-4 py-2 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-70"
                                style={{ backgroundColor: accent }}>
                            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Send feedback
                        </button>
                    </div>
                </DialogContent>
            </Dialog>

            <Toaster position="top-right" />
        </div>
    );
}

function Stat({ title, value }) {
    return (
        <div className="rounded-xl border border-slate-200 bg-white p-6">
            <p className="overline mb-2">{title}</p>
            <p className="font-display text-2xl tracking-tight text-slate-900">{value}</p>
        </div>
    );
}

function PhaseChip({ phase, accent }) {
    const icon = phase.status === "complete" ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
        : phase.status === "in_progress" ? <Clock className="w-4 h-4" style={{ color: accent }} />
        : <Circle className="w-4 h-4 text-slate-300" />;
    return (
        <div className="rounded-lg border border-slate-200 p-4 flex items-center gap-3 bg-white">
            {icon}
            <div>
                <p className="text-sm font-medium text-slate-900">{phase.name}</p>
                <p className="text-xs text-slate-500 capitalize">{phase.status.replace("_", " ")}</p>
            </div>
        </div>
    );
}

function StatusPill({ status }) {
    const tone = status === "delivered" || status === "approved" ? "emerald"
        : status === "review" || status === "pending" ? "amber"
        : status === "changes_requested" ? "rose"
        : "slate";
    const cls = tone === "emerald" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : tone === "amber" ? "bg-amber-50 text-amber-700 border-amber-200"
        : tone === "rose" ? "bg-rose-50 text-rose-700 border-rose-200"
        : "bg-slate-50 text-slate-600 border-slate-200";
    return <span className={`text-xs px-2 py-1 rounded-full capitalize border shrink-0 ${cls}`}>{status.replace("_", " ")}</span>;
}

function EmptyBox({ text }) {
    return <div className="rounded-xl border border-slate-200 bg-white p-8 text-slate-500 text-sm text-center">{text}</div>;
}

function formatDate(iso) {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return iso; }
}
